// components/Documentation.js
import React, { useState } from 'react';
import './Documentation.css';
import CodeBlock from './CodeBlock';

const Documentation = () => {
  // Add state to track active section
  const [activeSection, setActiveSection] = useState('getting-started');

  const apiExample = `# Basic API Reference
from cinder import ModelDebugger

CINDER_API_KEY = "your-api-key-here"

# Core methods
debugger = ModelDebugger(model, dataset, name="Model Name", api_key=CINDER_API_KEY)
results = debugger.analyze()                   # Run full analysis
importance = debugger.analyze_feature_importance() # Get feature importance
confidence = debugger.analyze_confidence()     # Analyze prediction confidence
suggestions = debugger.get_improvement_suggestions() # Get improvement suggestions
cv_results = debugger.perform_cross_validation(k_folds=5) # Cross-validation
`;

  const quickStartExample = `# Quick Start Example
from cinder import ModelDebugger
import torch
from torch.utils.data import DataLoader

CINDER_API_KEY = "your-api-key-here"

# Initialize with your model and data
model = YourModel()  # Your PyTorch model
test_loader = DataLoader(test_dataset, batch_size=32)

# Create debugger
debugger = ModelDebugger(model, test_loader, api_key=CINDER_API_KEY)

# Run analysis
results = debugger.analyze()
print(f"Model accuracy: {results['accuracy']:.4f}")

# Launch dashboard (optional)
debugger.launch_dashboard(port=8000)
`;

  const advancedExample = `# Advanced Usage: Custom Metrics
from cinder import ModelDebugger
import numpy as np

CINDER_API_KEY = "your-api-key-here"

# Define custom metric function
def f2_score(y_true, y_pred):
    """Calculate F2 score with higher weight on recall"""
    from sklearn.metrics import fbeta_score
    return fbeta_score(y_true, y_pred, beta=2, average='weighted')

# Initialize debugger with custom metrics
debugger = ModelDebugger(
    model=model, 
    dataset=test_dataset,
    custom_metrics={'f2_score': f2_score},
    api_key=CINDER_API_KEY
)

# Get results including custom metrics
results = debugger.analyze()
print(f"F2 Score: {results['f2_score']:.4f}")
`;

  return (
    <section className="documentation" id="documentation">
      <div className="section-header">
        <h2>Documentation</h2>
        <p>API reference and usage examples</p>
      </div>
      
      <div className="documentation-container">
        <div className="documentation-nav">
          <div className="nav-section">
            <h3>Getting Started</h3>
            <ul>
              <li>
                <a 
                  href="#getting-started" 
                  className={activeSection === 'getting-started' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('getting-started');
                  }}
                >
                  Installation & Quickstart
                </a>
              </li>
              <li>
                <a 
                  href="#frameworks" 
                  className={activeSection === 'frameworks' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('frameworks');
                  }}
                >
                  Framework Support
                </a>
              </li>
            </ul>
          </div>
          
          <div className="nav-section">
            <h3>Core API</h3>
            <ul>
              <li>
                <a 
                  href="#api-reference" 
                  className={activeSection === 'api-reference' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('api-reference');
                  }}
                >
                  API Reference
                </a>
              </li>
              <li>
                <a 
                  href="#model-debugger" 
                  className={activeSection === 'model-debugger' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('model-debugger');
                  }}
                >
                  ModelDebugger
                </a>
              </li>
              <li>
                <a 
                  href="#dataset-formats" 
                  className={activeSection === 'dataset-formats' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('dataset-formats');
                  }}
                >
                  Dataset Formats
                </a>
              </li>
            </ul>
          </div>
          
          <div className="nav-section">
            <h3>Advanced Usage</h3>
            <ul>
              <li>
                <a 
                  href="#custom-metrics" 
                  className={activeSection === 'custom-metrics' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('custom-metrics');
                  }}
                >
                  Custom Metrics
                </a>
              </li>
              <li>
                <a 
                  href="#error-analysis" 
                  className={activeSection === 'error-analysis' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('error-analysis');
                  }}
                >
                  Error Analysis
                </a>
              </li>
              <li>
                <a 
                  href="#production" 
                  className={activeSection === 'production' ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection('production');
                  }}
                >
                  Production Usage
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="documentation-content">
          {/* Getting Started Section */}
          {activeSection === 'getting-started' && (
            <div className="documentation-section">
              <h3>Installation & Quick Start</h3>
              <p>Get started with Cinder in minutes with pip install and a few lines of code:</p>
              
              <div className="install-command">
                <code>pip install cinder-ml</code>
              </div>
              
              <p>For framework-specific installations:</p>
              
              <div className="install-command">
                <code>pip install "cinder-ml[pytorch]"    # PyTorch support</code><br/>
                <code>pip install "cinder-ml[tensorflow]" # TensorFlow support</code><br/>
                <code>pip install "cinder-ml[all]"        # All frameworks</code>
              </div>
              
              <h4>Quick Start Example</h4>
              <div className="code-container">
                <CodeBlock code={quickStartExample} language="python" showLineNumbers={false} />
              </div>
            </div>
          )}
          
          {/* Framework Support Section */}
          {activeSection === 'frameworks' && (
            <div className="documentation-section">
              <h3>Framework Support</h3>
              <p>Cinder works with all major ML frameworks:</p>
              
              <div className="framework-support-grid">
                <div className="framework-support-item">
                  <h4>PyTorch</h4>
                  <ul>
                    <li>Full compatibility with nn.Module models</li>
                    <li>Works with DataLoader objects</li>
                    <li>Gradient-based visualizations</li>
                  </ul>
                </div>
                
                <div className="framework-support-item">
                  <h4>TensorFlow</h4>
                  <ul>
                    <li>Works with Sequential, Functional, and Subclassed models</li>
                    <li>Compatible with tf.data.Dataset</li>
                    <li>TensorBoard integration</li>
                  </ul>
                </div>
                
                <div className="framework-support-item">
                  <h4>scikit-learn</h4>
                  <ul>
                    <li>All estimator types supported</li>
                    <li>Tree-based model visualizations</li>
                    <li>Cross-validation analysis</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* API Reference Section */}
          {activeSection === 'api-reference' && (
            <div className="documentation-section">
              <h3>API Reference</h3>
              <p>The main interface to Cinder is the ModelDebugger class, which provides comprehensive analysis functionality for machine learning models:</p>
              
              <div className="code-container">
                <CodeBlock code={apiExample} language="python" showLineNumbers={false} />
              </div>
              
              <div className="method-list">
                <div className="method-item">
                  <h4>analyze()</h4>
                  <p>Runs comprehensive analysis on the model, returning metrics, error analysis, and confusion matrix.</p>
                  <div className="method-signature">Returns: Dict with analysis results</div>
                </div>
                
                <div className="method-item">
                  <h4>analyze_feature_importance()</h4>
                  <p>Analyzes the importance of each feature in the model's predictions.</p>
                  <div className="method-signature">Returns: Dict with feature names and importance scores</div>
                </div>
                
                <div className="method-item">
                  <h4>analyze_confidence()</h4>
                  <p>Analyzes the confidence of model predictions, identifying over/under-confident predictions.</p>
                  <div className="method-signature">Returns: Dict with confidence metrics</div>
                </div>
              </div>
            </div>
          )}
          
          {/* ModelDebugger Section */}
          {activeSection === 'model-debugger' && (
            <div className="documentation-section">
              <h3>ModelDebugger Class</h3>
              <p>The ModelDebugger class is the main interface to Cinder's functionality:</p>
              
              <div className="method-item">
                <h4>ModelDebugger(model, dataset, name=None, feature_names=None)</h4>
                <p>Initialize a new ModelDebugger instance with a model and dataset.</p>
                <div className="method-params">
                  <div><strong>model</strong>: A trained model (PyTorch, TensorFlow, or scikit-learn)</div>
                  <div><strong>dataset</strong>: Dataset that yields (input, target) pairs</div>
                  <div><strong>name</strong>: Optional name for the model</div>
                  <div><strong>feature_names</strong>: Optional list of feature names</div>
                </div>
              </div>
              
              <p>The ModelDebugger adapts to the framework of your model automatically, providing consistent functionality across PyTorch, TensorFlow, and scikit-learn.</p>
            </div>
          )}
          
          {/* Dataset Formats Section */}
          {activeSection === 'dataset-formats' && (
            <div className="documentation-section">
              <h3>Dataset Formats</h3>
              <p>Cinder works with various dataset formats, requiring only that they implement the iterator protocol:</p>
              
              <div className="code-container">
                <CodeBlock code={`class YourDataset:
    def __iter__(self):
        # Initialize iteration
        return self
    
    def __next__(self):
        # Return next batch of (inputs, targets)
        # Raise StopIteration when done
        if no_more_data:
            raise StopIteration
        return inputs_batch, targets_batch`} language="python" showLineNumbers={false} />
              </div>
              
              <p>For PyTorch, Cinder works directly with DataLoader objects. For TensorFlow and scikit-learn, you can use wrapper classes provided in the examples.</p>
            </div>
          )}
          
          {/* Custom Metrics Section */}
          {activeSection === 'custom-metrics' && (
            <div className="documentation-section">
              <h3>Custom Metrics</h3>
              <p>You can define and use custom metrics with Cinder to extend its analysis capabilities:</p>
              
              <div className="code-container">
                <CodeBlock code={advancedExample} language="python" showLineNumbers={false} />
              </div>
              
              <p>Custom metrics are included in the analysis results dictionary and can be visualized in the dashboard.</p>
            </div>
          )}
          
          {/* Error Analysis Section */}
          {activeSection === 'error-analysis' && (
            <div className="documentation-section">
              <h3>Error Analysis</h3>
              <p>Cinder provides advanced error analysis capabilities to help identify patterns in model errors:</p>
              
              <div className="error-types">
                <div className="error-type-item">
                  <h4>Confusion Matrix</h4>
                  <p>Detailed breakdown of predictions by class, highlighting where the model gets confused.</p>
                </div>
                
                <div className="error-type-item">
                  <h4>Error Clustering</h4>
                  <p>Automatically groups similar errors to identify systematic patterns in model failures.</p>
                </div>
                
                <div className="error-type-item">
                  <h4>Confidence Analysis</h4>
                  <p>Identifies over-confident and under-confident predictions to improve calibration.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Production Usage Section */}
          {activeSection === 'production' && (
            <div className="documentation-section">
              <h3>Production Usage</h3>
              <p>Cinder can be integrated into production systems for continuous model monitoring:</p>
              
              <div className="code-container">
                <CodeBlock code={`from cinder import ModelMonitor

# Initialize monitor
monitor = ModelMonitor(
    model=production_model,
    log_dir="logs/",
    alert_threshold=0.15  # Alert if drift > 15%
)

# Log predictions in production
@app.post("/predict")
def predict(data):
    prediction = model.predict(data)
    
    # Log the prediction for monitoring
    monitor.log_prediction(data, prediction)
    
    return {"prediction": prediction}`} language="python" showLineNumbers={false} />
              </div>
              
              <p>The ModelMonitor class tracks prediction drift, data distribution changes, and model performance over time.</p>
            </div>
          )}
          
          <div className="documentation-cta">
            <p>For additional documentation, examples, and support with implementing Cinder in your project:</p>
            <a href="#frameworks" className="btn btn-primary">View Framework Examples</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Documentation;